import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiGet, apiSend, resetCsrfToken } from './api'

export interface CurrentUser {
  userId: string
  displayName: string
  email: string
  roles: string[]
  isAdmin: boolean
}

export type LoginNext = 'mfa' | 'password-setup' | 'home'

export interface LoginResponse {
  next: LoginNext
}

export const sessionKey = ['session'] as const

// Un 401 devient null : c'est la reponse normale d'un visiteur non connecte.
export function useCurrentUser() {
  return useQuery({
    queryKey: sessionKey,
    retry: false,
    staleTime: 30_000,
    queryFn: async (): Promise<CurrentUser | null> => {
      try {
        return await apiGet<CurrentUser>('/auth/me')
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          return null
        }
        throw error
      }
    },
  })
}

function useSessionMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sessionKey }),
  })
}

export function useLogin() {
  return useSessionMutation((credentials: { email: string; password: string }) =>
    apiSend<LoginResponse>('/auth/login', 'POST', credentials),
  )
}

export function useRegister() {
  return useMutation({
    mutationFn: (account: { email: string; password: string; displayName: string }) =>
      apiSend<{ userId: string }>('/auth/register', 'POST', account),
  })
}

export function useVerifyMfa() {
  return useSessionMutation(
    ({ code, useRecoveryCode }: { code: string; useRecoveryCode: boolean }) =>
      apiSend<LoginResponse>(useRecoveryCode ? '/auth/mfa/recovery' : '/auth/mfa', 'POST', {
        code,
      }),
  )
}

export function useSetupPassword() {
  return useSessionMutation((passwords: { password: string; confirmation: string }) =>
    apiSend<LoginResponse>('/auth/password/setup', 'POST', passwords),
  )
}

export function useConfirmEmail() {
  return useMutation({
    mutationFn: (link: { userId: string; token: string }) =>
      apiSend<null>('/auth/email/confirm', 'POST', link),
  })
}

export function useResendConfirmation() {
  return useMutation({
    mutationFn: (email: string) => apiSend<null>('/auth/email/resend', 'POST', { email }),
  })
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => apiSend<null>('/auth/password/forgot', 'POST', { email }),
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (reset: {
      userId: string
      token: string
      password: string
      confirmation: string
    }) => apiSend<null>('/auth/password/reset', 'POST', reset),
  })
}

export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => apiSend<null>('/auth/logout', 'POST'),
    onSuccess: async () => {
      // Le jeton est lie a la session fermee : le garder ferait echouer la connexion suivante.
      resetCsrfToken()
      queryClient.setQueryData(sessionKey, null)
      await queryClient.invalidateQueries()
    },
  })
}
